import React, { useEffect, useState } from "react";
import Layout, { Container } from "../components/layout";
import { getCookie } from "../config/webStorage";
import { apiurl } from "../config/config";
import * as XLSX from "xlsx";

/* ---------- CATEGORY MASTER ---------- */
const MATERIAL_CATEGORIES = {
  packaging_material: [
    { value: "jar", label: "Jar" },
    { value: "tape", label: "Tape" },
    { value: "carton_box", label: "Carton Box" },
    { value: "spoon", label: "Spoon" },
    { value: "diet_chart", label: "Diet Chart" },
    { value: "shrink", label: "Shrink" },
    { value: "monocarton", label: "Monocarton" },
    { value: "label", label: "Label" },
    { value: "gel", label: "Gel" },
    { value: "bag", label: "Bag" },
    { value: "empty_capsule", label: "Empty Capsule" },
  ],
  raw_material: [
    { value: "herb", label: "Herb" },
    { value: "powder", label: "Powder" },
    { value: "extract", label: "Extract" },
    { value: "chemical", label: "Chemical" },
    { value: "oil", label: "Oil" },
    { value: "juice", label: "Juice" },
    { value: "ghee", label: "Ghee" },
  ],
};

const Inventory = () => {
  const token = getCookie("SWFMSToken");

  /* ---------- STATES ---------- */
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [materialType, setMaterialType] = useState("");
  const [category, setCategory] = useState("");
  const [stockStatus, setStockStatus] = useState("");

  /* ---------- FETCH INVENTORY ---------- */
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const res = await fetch(`${apiurl}/api/material/inventory`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        });
        const result = await res.json();
        if (result.success) setInventory(result.data || []);
      } catch (err) {
        console.error("Inventory fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInventory();
  }, [token]);

  /* ---------- FILTER LOGIC ---------- */
  const filteredInventory = inventory.filter((item) => {
    const matchSearch = item.name
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const matchType = materialType
      ? item.materialType === materialType
      : true;

    const matchCategory = category ? item.category === category : true;

    const matchStock =
      stockStatus === ""
        ? true
        : stockStatus === "in_stock"
        ? item.quantity > item.minStockLevel
        : stockStatus === "low_stock"
        ? item.quantity <= item.minStockLevel && item.quantity > 0
        : item.quantity === 0;

    return matchSearch && matchType && matchCategory && matchStock;
  });

  /* ---------- EXCEL DOWNLOAD ---------- */
  const downloadExcel = () => {
    if (!filteredInventory.length) return;

    const excelData = filteredInventory.map((item) => ({
      "Material ID": item.materialId,
      "Material Name": item.name,
      "Material Type": item.materialType,
      Category: item.category,
      Quantity: item.quantity,
      // "Unit Price": item.pricePerUnit,
      "Min Stock": item.minStockLevel,
      "Unit": item.unit,
      Status:
        item.quantity === 0
          ? "Out of Stock"
          : item.quantity <= item.minStockLevel
          ? "Low Stock"
          : "In Stock",
      // Supplier: item.supplier || "",
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory");

    XLSX.writeFile(
      wb,
      `inventory_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  /* ---------- RESET ---------- */
  const resetFilters = () => {
    setSearch("");
    setMaterialType("");
    setCategory("");
    setStockStatus("");
  };

  /* ---------- RENDER ---------- */
  return (
    <Layout>
      <Container overflow="overflow-scroll">
        <div className="p-6">

          {/* HEADER */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Inventory</h1>
            <input
              type="text"
              placeholder="Search material..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mt-3 md:mt-0 px-4 py-2 border rounded w-full md:w-64"
            />
          </div>

          {/* FILTERS */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">

            <select
              value={materialType}
              onChange={(e) => {
                setMaterialType(e.target.value);
                setCategory("");
              }}
              className="p-2 border rounded"
            >
              <option value="">All Types</option>
              <option value="raw_material">Raw Material</option>
              <option value="packaging_material">Packaging Material</option>
            </select>

            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="p-2 border rounded"
            >
              <option value="">All Categories</option>
              {(materialType
                ? MATERIAL_CATEGORIES[materialType]
                : Object.values(MATERIAL_CATEGORIES).flat()
              ).map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>

            <select
              value={stockStatus}
              onChange={(e) => setStockStatus(e.target.value)}
              className="p-2 border rounded"
            >
              <option value="">All Stock</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>

            <button
              onClick={downloadExcel}
              className="bg-[#4B49AC] text-white rounded px-4 py-2"
            >
              Download Excel
            </button>

            <button
              onClick={resetFilters}
              className="bg-gray-200 rounded px-4 py-2"
            >
              Reset
            </button>
          </div>

          {/* TABLE */}
          {loading ? (
            <p className="text-center text-gray-500">Loading inventory...</p>
          ) : (
            <div className="overflow-x-auto bg-white shadow rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="border p-2">Material ID</th>
                    <th className="border p-2">Name</th>
                    <th className="border p-2">Type</th>
                    <th className="border p-2">Category</th>
                    <th className="border p-2">Qty</th>
                    <th className="border p-2">Unit Price</th>
                    <th className="border p-2">Min Stock</th>
                    <th className="border p-2">Status</th>
                    <th className="border p-2">Supplier</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.map((item, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="border p-2">{item.materialId}</td>
                      <td className="border p-2">{item.name}</td>
                      <td className="border p-2">{item.materialType}</td>
                      <td className="border p-2">{item.category}</td>
                      <td className="border p-2">{item.quantity}</td>
                      <td className="border p-2">₹{item.pricePerUnit}</td>
                      <td className="border p-2">{item.minStockLevel}</td>
                      <td className="border p-2">
                        {item.quantity === 0 ? (
                          <span className="text-red-600">Out</span>
                        ) : item.quantity <= item.minStockLevel ? (
                          <span className="text-yellow-600">Low</span>
                        ) : (
                          <span className="text-green-600">In</span>
                        )}
                      </td>
                      <td className="border p-2">{item.supplier || "-"}</td>
                    </tr>
                  ))}

                  {filteredInventory.length === 0 && (
                    <tr>
                      <td colSpan="9" className="text-center p-6 text-gray-500">
                        No inventory found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Container>
    </Layout>
  );
};

export default Inventory;
